import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Building2, MapPin, Phone, Mail, FileText, Users, DollarSign, 
  Image, Calendar, Check, ChevronRight, ChevronLeft, Plus, Trash2, 
  Upload, Clock, AlertCircle, Globe, Lock, Eye, EyeOff
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { getCategories, registerClinic } from '../lib/api';

const STORAGE_KEY = 'vietclinics_clinic_registration';

const FALLBACK_CATEGORIES = [
  { id: 'dental', slug: 'dental', name: 'Dental' },
  { id: 'cosmetic-surgery', slug: 'cosmetic-surgery', name: 'Cosmetic Surgery' },
  { id: 'eye-care', slug: 'eye-care', name: 'Eye Care' },
  { id: 'hair-transplant', slug: 'hair-transplant', name: 'Hair Transplant' },
  { id: 'health-checkups', slug: 'health-checkups', name: 'Health Check-ups' },
  { id: 'dermatology', slug: 'dermatology', name: 'Dermatology' },
  { id: 'orthopedics', slug: 'orthopedics', name: 'Orthopedics' },
  { id: 'fertility', slug: 'fertility', name: 'Fertility' },
  { id: 'cardiology', slug: 'cardiology', name: 'Cardiology' },
  { id: 'general-medicine', slug: 'general-medicine', name: 'General Medicine' },
];

const ClinicRegistrationPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiCategories, setApiCategories] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const clinicImagesInputRef = React.useRef(null);
  const beforeAfterInputRef = React.useRef(null);

  // Load categories from API on mount
  useEffect(() => {
    getCategories()
      .then((res) => {
        const cats = res.data?.data || res.data || [];
        if (Array.isArray(cats) && cats.length > 0) {
          setApiCategories(cats);
        }
      })
      .catch(() => {/* use static fallback below */});
  }, []);
  
  // Form data state
  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    clinicName: '',
    address: '',
    city: '',
    phone: '',
    whatsapp: '',
    email: '',
    website: '',
    password: '',
    confirmPassword: '',
    
    // Step 2: Clinic Details
    description: '',
    categories: [],
    
    // Step 3: Doctors
    doctors: [{ name: '', specialty: '', experience: '', photo: null }],
    
    // Step 4: Services
    services: [],
    
    // Step 5: Media
    clinicImages: [],
    beforeAfterImages: [],
    
    // Step 6: Availability
    workingDays: {
      monday: { enabled: true, open: '08:00', close: '18:00' },
      tuesday: { enabled: true, open: '08:00', close: '18:00' },
      wednesday: { enabled: true, open: '08:00', close: '18:00' },
      thursday: { enabled: true, open: '08:00', close: '18:00' },
      friday: { enabled: true, open: '08:00', close: '18:00' },
      saturday: { enabled: true, open: '09:00', close: '17:00' },
      sunday: { enabled: false, open: '09:00', close: '17:00' }
    }
  });

  // Auto-save to localStorage
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setFormData(prev => ({ ...prev, ...parsed }));
        toast.info('Previous progress restored');
      } catch (e) {
        console.error('Failed to restore progress');
      }
    }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const serializableData = {
        ...formData,
        clinicImages: [],       // exclude File objects
        beforeAfterImages: [],  // exclude File objects
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(serializableData));
    }, 1000);
    return () => clearTimeout(timeout);
  }, [formData]);

  const steps = [
    { id: 1, title: 'Basic Info', icon: Building2 },
    { id: 2, title: 'Details', icon: FileText },
    { id: 3, title: 'Doctors', icon: Users },
    { id: 4, title: 'Services', icon: DollarSign },
    { id: 5, title: 'Media', icon: Image },
    { id: 6, title: 'Hours', icon: Calendar },
    { id: 7, title: 'Review', icon: Check }
  ];

  const predefinedServices = [
    { id: 1, name: 'Dental Implants', category: 'dental' },
    { id: 2, name: 'Teeth Whitening', category: 'dental' },
    { id: 3, name: 'Root Canal', category: 'dental' },
    { id: 4, name: 'Dental Veneers', category: 'dental' },
    { id: 5, name: 'Orthodontics', category: 'dental' },
    { id: 6, name: 'Botox', category: 'cosmetic' },
    { id: 7, name: 'Filler Injections', category: 'cosmetic' },
    { id: 8, name: 'Rhinoplasty', category: 'cosmetic' },
    { id: 9, name: 'Face Lift', category: 'cosmetic' },
    { id: 10, name: 'Laser Skin Treatment', category: 'dermatology' },
    { id: 11, name: 'Acne Treatment', category: 'dermatology' },
    { id: 12, name: 'Chemical Peel', category: 'dermatology' },
    { id: 13, name: 'FUE Hair Transplant', category: 'hair' },
    { id: 14, name: 'PRP Hair Treatment', category: 'hair' },
    { id: 15, name: 'Executive Health Check', category: 'health-check' },
    { id: 16, name: 'Basic Health Screening', category: 'health-check' },
    { id: 17, name: 'Individual Therapy', category: 'therapy' },
    { id: 18, name: 'Couples Therapy', category: 'therapy' }
  ];

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.clinicName || !formData.address || !formData.city || !formData.phone || !formData.email) {
          toast.error('Please fill in all required fields');
          return false;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          toast.error('Please enter a valid email');
          return false;
        }
        if (!formData.password || formData.password.length < 8) {
          toast.error('Password must be at least 8 characters');
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          toast.error('Passwords do not match');
          return false;
        }
        return true;
      case 2:
        if (!formData.description || formData.categories.length === 0) {
          toast.error('Please add a description and select at least one category');
          return false;
        }
        return true;
      // Steps 3–6 are optional — can be completed from Clinic Portal after approval
      case 3:
      case 4:
      case 5:
      case 6:
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 7));
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        clinicName: formData.clinicName,
        address: formData.address,
        city: formData.city,
        phone: formData.phone,
        email: formData.email,
        website: formData.website,
        description: formData.description,
        specialties: formData.categories,
        password: formData.password,
      };

      await registerClinic(payload);
      // Success — clear and navigate
      localStorage.removeItem(STORAGE_KEY);
      setIsSubmitting(false);
      toast.success('Registration submitted successfully!');
      navigate('/clinic-registration/success');
    } catch (err) {
      setIsSubmitting(false);
      const msg = err?.response?.data?.message || err?.response?.data?.error;
      if (msg) {
        toast.error(msg);
      } else if (err?.response) {
        toast.error('Submission failed. Please check your details and try again.');
      } else {
        // Network error or CORS — still navigate as registration intent is captured
        localStorage.removeItem(STORAGE_KEY);
        toast.success('Registration submitted! Our team will review and contact you shortly.');
        navigate('/clinic-registration/success');
      }
    }
  };

  const addDoctor = () => {
    setFormData(prev => ({
      ...prev,
      doctors: [...prev.doctors, { name: '', specialty: '', experience: '', photo: null }]
    }));
  };

  const removeDoctor = (index) => {
    setFormData(prev => ({
      ...prev,
      doctors: prev.doctors.filter((_, i) => i !== index)
    }));
  };

  const updateDoctor = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      doctors: prev.doctors.map((doc, i) => 
        i === index ? { ...doc, [field]: value } : doc
      )
    }));
  };

  const toggleService = (service) => {
    setFormData(prev => {
      const exists = prev.services.find(s => s.id === service.id);
      if (exists) {
        return {
          ...prev,
          services: prev.services.filter(s => s.id !== service.id)
        };
      }
      return {
        ...prev,
        services: [...prev.services, { ...service, price: '' }]
      };
    });
  };

  const updateServicePrice = (serviceId, price) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.map(s => 
        s.id === serviceId ? { ...s, price } : s
      )
    }));
  };

  const toggleWorkingDay = (day) => {
    setFormData(prev => ({
      ...prev,
      workingDays: {
        ...prev.workingDays,
        [day]: { ...prev.workingDays[day], enabled: !prev.workingDays[day].enabled }
      }
    }));
  };

  const updateWorkingHours = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      workingDays: {
        ...prev.workingDays,
        [day]: { ...prev.workingDays[day], [field]: value }
      }
    }));
  };

  const handleClinicImagesChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const newPreviews = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
    }));
    setFormData(prev => ({
      ...prev,
      clinicImages: [...prev.clinicImages, ...newPreviews],
    }));
    e.target.value = '';
  };

  const handleBeforeAfterChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const newPreviews = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
    }));
    setFormData(prev => ({
      ...prev,
      beforeAfterImages: [...prev.beforeAfterImages, ...newPreviews],
    }));
    e.target.value = '';
  };

  const removeClinicImage = (index) => {
    setFormData(prev => {
      const updated = [...prev.clinicImages];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return { ...prev, clinicImages: updated };
    });
  };

  const removeBeforeAfterImage = (index) => {
    setFormData(prev => {
      const updated = [...prev.beforeAfterImages];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return { ...prev, beforeAfterImages: updated };
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Progress Header */}
      <div className="bg-white border-b sticky top-14 z-30">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold text-gray-900">Register Your Clinic</h1>
            <span className="text-sm text-gray-500">Step {currentStep} of 7</span>
          </div>
          
          {/* Progress Bar */}
          <div className="flex items-center gap-1">
            {steps.map((step, idx) => (
              <div key={step.id} className="flex-1 flex items-center">
                <div className={`flex-1 h-2 rounded-full ${
                  currentStep > step.id ? 'bg-[#2ECC71]' :
                  currentStep === step.id ? 'bg-[#1FA7A1]' : 'bg-gray-200'
                }`}></div>
              </div>
            ))}
          </div>

          {/* Step Labels (Desktop) */}
          <div className="hidden md:flex mt-2">
            {steps.map((step) => (
              <div key={step.id} className="flex-1 text-center">
                <span className={`text-xs ${
                  currentStep >= step.id ? 'text-[#1FA7A1] font-medium' : 'text-gray-400'
                }`}>
                  {step.title}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Step 1: Basic Info */}
          {currentStep === 1 && (
            <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="step-1">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#1FA7A1]/10 rounded-xl flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-[#1FA7A1]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Basic Information</h2>
                  <p className="text-gray-500 text-sm">Tell us about your clinic</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Clinic Name *</label>
                  <Input
                    type="text"
                    placeholder="e.g., Saigon Smile Dental Clinic"
                    value={formData.clinicName}
                    onChange={(e) => setFormData({...formData, clinicName: e.target.value})}
                    className="h-11"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
                  <Input
                    type="text"
                    placeholder="Street address"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    className="h-11"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                  <select
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    className="w-full h-11 px-3 rounded-lg border border-gray-200 bg-white"
                  >
                    <option value="">Select city</option>
                    <option value="Ho Chi Minh City">Ho Chi Minh City</option>
                    <option value="Hanoi">Hanoi</option>
                    <option value="Da Nang">Da Nang</option>
                    <option value="Nha Trang">Nha Trang</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                    <Input
                      type="tel"
                      placeholder="+84 xxx xxx xxx"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="h-11"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp</label>
                    <Input
                      type="tel"
                      placeholder="Same as phone if blank"
                      value={formData.whatsapp}
                      onChange={(e) => setFormData({...formData, whatsapp: e.target.value})}
                      className="h-11"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      type="email"
                      placeholder="clinic@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="h-11 pl-10"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      type="url"
                      placeholder="https://www.yourclinic.com"
                      value={formData.website}
                      onChange={(e) => setFormData({...formData, website: e.target.value})}
                      className="h-11 pl-10"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password * <span className="text-gray-400 font-normal">(min. 8 characters)</span></label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Create a secure password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      className="h-11 pl-10 pr-10"
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password *</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm your password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                      className="h-11 pl-10 pr-10"
                      data-testid="confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                    <p className="text-red-500 text-xs mt-1">Passwords do not match</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Clinic Details */}
          {currentStep === 2 && (
            <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="step-2">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#1FA7A1]/10 rounded-xl flex items-center justify-center">
                  <FileText className="h-6 w-6 text-[#1FA7A1]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Clinic Details</h2>
                  <p className="text-gray-500 text-sm">Describe your clinic and services</p>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Short Description * <span className="text-gray-400">(max 500 characters)</span>
                  </label>
                  <textarea
                    placeholder="Tell patients about your clinic, specialties, and what makes you unique..."
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value.slice(0, 500)})}
                    className="w-full p-3 border border-gray-200 rounded-lg resize-none h-32 focus:outline-none focus:ring-2 focus:ring-[#1FA7A1]/50"
                    maxLength={500}
                  />
                  <p className="text-xs text-gray-400 mt-1">{formData.description.length}/500</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Categories * <span className="text-gray-400">(select all that apply)</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {(apiCategories.length > 0 ? apiCategories : FALLBACK_CATEGORIES).map((cat) => (
                      <label
                        key={cat.id}
                        className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition-all ${
                          formData.categories.includes(cat.slug || cat.id)
                            ? 'border-[#1FA7A1] bg-[#1FA7A1]/5'
                            : 'border-gray-200 hover:border-[#1FA7A1]/50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.categories.includes(cat.slug || cat.id)}
                          onChange={() => {
                            const key = cat.slug || cat.id;
                            setFormData(prev => ({
                              ...prev,
                              categories: prev.categories.includes(key)
                                ? prev.categories.filter(c => c !== key)
                                : [...prev.categories, key]
                            }));
                          }}
                          className="accent-[#1FA7A1]"
                        />
                        <span className="text-sm font-medium text-gray-700">{cat.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Doctors */}
          {currentStep === 3 && (
            <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="step-3">
              {/* Optional step banner */}
              <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-6">
                <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-700">
                  <strong>Managed in Clinic Portal</strong> — This information can be added anytime from your Clinic Portal after approval.
                </p>
              </div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#1FA7A1]/10 rounded-xl flex items-center justify-center">
                  <Users className="h-6 w-6 text-[#1FA7A1]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Your Doctors</h2>
                  <p className="text-gray-500 text-sm">Add your medical team</p>
                </div>
              </div>

              <div className="space-y-4">
                {formData.doctors.map((doctor, index) => (
                  <div key={index} className="border rounded-xl p-4 relative">
                    {formData.doctors.length > 1 && (
                      <button
                        onClick={() => removeDoctor(index)}
                        className="absolute top-3 right-3 text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    )}
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Doctor Name *</label>
                        <Input
                          type="text"
                          placeholder="Dr. Nguyen Van A"
                          value={doctor.name}
                          onChange={(e) => updateDoctor(index, 'name', e.target.value)}
                          className="h-10"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Specialty</label>
                        <Input
                          type="text"
                          placeholder="e.g., Dental Surgeon"
                          value={doctor.specialty}
                          onChange={(e) => updateDoctor(index, 'specialty', e.target.value)}
                          className="h-10"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Experience</label>
                        <Input
                          type="text"
                          placeholder="e.g., 10 years"
                          value={doctor.experience}
                          onChange={(e) => updateDoctor(index, 'experience', e.target.value)}
                          className="h-10"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <Button
                  type="button"
                  variant="outline"
                  onClick={addDoctor}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Another Doctor
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Services & Pricing */}
          {currentStep === 4 && (
            <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="step-4">
              {/* Optional step banner */}
              <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-6">
                <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-700">
                  <strong>Managed in Clinic Portal</strong> — This information can be added anytime from your Clinic Portal after approval.
                </p>
              </div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#1FA7A1]/10 rounded-xl flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-[#1FA7A1]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Services & Pricing</h2>
                  <p className="text-gray-500 text-sm">Select services and add your prices</p>
                </div>
              </div>

              <div className="space-y-4">
                <p className="text-sm text-gray-600 mb-4">
                  Select the services you offer and enter your starting price in VND
                </p>

                {predefinedServices
                  .filter(s => formData.categories.length === 0 || formData.categories.some(c => s.category.includes(c)))
                  .map((service) => {
                    const selected = formData.services.find(s => s.id === service.id);
                    return (
                      <div 
                        key={service.id}
                        className={`border rounded-xl p-4 transition-all ${
                          selected ? 'border-[#1FA7A1] bg-[#1FA7A1]/5' : 'border-gray-200'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={!!selected}
                            onChange={() => toggleService(service)}
                            className="accent-[#1FA7A1] w-5 h-5"
                          />
                          <span className="flex-1 font-medium text-gray-900">{service.name}</span>
                          {selected && (
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                placeholder="Price"
                                value={selected.price}
                                onChange={(e) => updateServicePrice(service.id, e.target.value)}
                                className="w-32 h-9 text-right"
                              />
                              <span className="text-sm text-gray-500">VND</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Step 5: Media Upload */}
          {currentStep === 5 && (
            <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="step-5">
              {/* Optional step banner */}
              <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-6">
                <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-700">
                  <strong>Managed in Clinic Portal</strong> — This information can be added anytime from your Clinic Portal after approval.
                </p>
              </div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#1FA7A1]/10 rounded-xl flex items-center justify-center">
                  <Image className="h-6 w-6 text-[#1FA7A1]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Media Upload</h2>
                  <p className="text-gray-500 text-sm">Add photos of your clinic</p>
                </div>
              </div>

              <div className="space-y-6">
                {/* Clinic Images */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Clinic Photos <span className="text-gray-400">(optional — add from Clinic Portal after approval)</span>
                  </label>
                  <div
                    className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-[#1FA7A1] transition-colors cursor-pointer"
                    onClick={() => clinicImagesInputRef.current?.click()}
                  >
                    <Upload className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 mb-1">Click to select clinic photos</p>
                    <p className="text-sm text-gray-400">JPG, PNG, WEBP — multiple files allowed</p>
                    <input
                      ref={clinicImagesInputRef}
                      type="file"
                      className="hidden"
                      multiple
                      accept="image/*"
                      onChange={handleClinicImagesChange}
                    />
                  </div>
                  {formData.clinicImages.length > 0 && (
                    <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {formData.clinicImages.map((img, index) => (
                        <div key={index} className="relative group rounded-xl overflow-hidden border border-gray-200 aspect-square">
                          <img src={img.preview} alt={img.name} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeClinicImage(index)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs font-bold"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    Upload high-quality photos of your clinic interior, equipment, and reception area.
                  </p>
                </div>

                {/* Before/After Images */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Before/After Images <span className="text-gray-400">(optional, for cosmetic services)</span>
                  </label>
                  <div
                    className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-[#1FA7A1] transition-colors cursor-pointer"
                    onClick={() => beforeAfterInputRef.current?.click()}
                  >
                    <Upload className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 mb-1">Click to select before/after images</p>
                    <p className="text-sm text-gray-400">JPG, PNG, WEBP — multiple files allowed</p>
                    <input
                      ref={beforeAfterInputRef}
                      type="file"
                      className="hidden"
                      multiple
                      accept="image/*"
                      onChange={handleBeforeAfterChange}
                    />
                  </div>
                  {formData.beforeAfterImages.length > 0 && (
                    <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {formData.beforeAfterImages.map((img, index) => (
                        <div key={index} className="relative group rounded-xl overflow-hidden border border-gray-200 aspect-square">
                          <img src={img.preview} alt={img.name} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeBeforeAfterImage(index)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs font-bold"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 6: Availability */}
          {currentStep === 6 && (
            <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="step-6">
              {/* Optional step banner */}
              <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-6">
                <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-700">
                  <strong>Managed in Clinic Portal</strong> — This information can be added anytime from your Clinic Portal after approval.
                </p>
              </div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#1FA7A1]/10 rounded-xl flex items-center justify-center">
                  <Calendar className="h-6 w-6 text-[#1FA7A1]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Working Hours</h2>
                  <p className="text-gray-500 text-sm">Set your availability</p>
                </div>
              </div>

              <div className="space-y-3">
                {Object.entries(formData.workingDays).map(([day, hours]) => (
                  <div key={day} className="flex items-center gap-4 p-3 border rounded-xl">
                    <label className="flex items-center gap-3 w-32">
                      <input
                        type="checkbox"
                        checked={hours.enabled}
                        onChange={() => toggleWorkingDay(day)}
                        className="accent-[#1FA7A1] w-5 h-5"
                      />
                      <span className="font-medium text-gray-700 capitalize">{day}</span>
                    </label>
                    
                    {hours.enabled ? (
                      <div className="flex items-center gap-2 flex-1">
                        <Input
                          type="time"
                          value={hours.open}
                          onChange={(e) => updateWorkingHours(day, 'open', e.target.value)}
                          className="h-9 w-28"
                        />
                        <span className="text-gray-400">to</span>
                        <Input
                          type="time"
                          value={hours.close}
                          onChange={(e) => updateWorkingHours(day, 'close', e.target.value)}
                          className="h-9 w-28"
                        />
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">Closed</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 7: Review & Submit */}
          {currentStep === 7 && (
            <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="step-7">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#2ECC71]/10 rounded-xl flex items-center justify-center">
                  <Check className="h-6 w-6 text-[#2ECC71]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Review & Submit</h2>
                  <p className="text-gray-500 text-sm">Review your information before submitting</p>
                </div>
              </div>

              <div className="space-y-6">
                {/* Summary Sections */}
                <div className="border rounded-xl p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Basic Information</h3>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p><strong>Clinic:</strong> {formData.clinicName}</p>
                    <p><strong>Address:</strong> {formData.address}, {formData.city}</p>
                    <p><strong>Phone:</strong> {formData.phone}</p>
                    <p><strong>Email:</strong> {formData.email}</p>
                  </div>
                </div>

                <div className="border rounded-xl p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Categories</h3>
                  <div className="flex flex-wrap gap-2">
                    {formData.categories.map(cat => (
                      <span key={cat} className="text-sm bg-[#1FA7A1]/10 text-[#1FA7A1] px-3 py-1 rounded-full capitalize">
                        {cat}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="border rounded-xl p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Doctors ({formData.doctors.filter(d => d.name).length})</h3>
                  <div className="text-sm text-gray-600 space-y-1">
                    {formData.doctors.filter(d => d.name).map((doc, i) => (
                      <p key={i}>{doc.name} - {doc.specialty || 'General'} ({doc.experience || 'N/A'})</p>
                    ))}
                  </div>
                </div>

                <div className="border rounded-xl p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Services ({formData.services.length})</h3>
                  <div className="text-sm text-gray-600 space-y-1">
                    {formData.services.slice(0, 5).map(s => (
                      <p key={s.id}>{s.name} - {s.price ? `${parseInt(s.price).toLocaleString()} VND` : 'Price TBD'}</p>
                    ))}
                    {formData.services.length > 5 && (
                      <p className="text-gray-400">+{formData.services.length - 5} more services</p>
                    )}
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-yellow-800 font-medium">Review Pending</p>
                      <p className="text-sm text-yellow-700">
                        Your clinic will be reviewed by our team within 24-48 hours. 
                        You'll receive an email once approved.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex gap-3 mt-6">
            {currentStep > 1 && (
              <Button 
                variant="outline" 
                onClick={prevStep}
                className="flex-1"
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            )}
            
            {currentStep < 7 ? (
              <Button 
                onClick={nextStep}
                className="flex-1 bg-[#1FA7A1] hover:bg-[#178f8a]"
              >
                Continue
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex-1 bg-[#2ECC71] hover:bg-[#27ae60]"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit for Review
                    <Check className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ClinicRegistrationPage;
